import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Heading,
    InputFormField,
    Stack,
    Text,
    TextAreaFormField,
} from '@indeed/ifl-components';
import {
    useTestRbsCurrentUserLazyQuery,
    useTestRbsGetResumesLazyQuery,
    useTestRbsCreateEmptyResumeMutation,
    useTestRbsUpdateResumeMutation,
    useTestRbsAddWorkExperiencesMutation,
    useTestRbsUpdateAutoApplyMutation,
    useTestRbsAutoApplyInfoLazyQuery,
} from './__generated__/TestRbsPage.generated';
import {
    ProctorOverridesPanel,
    SUPPORTED_PROCTORS,
    proctorStateFromPrforceGroups,
    useProctorOverrides,
} from './proctorOverrides';
import { resolveLinkOrigin } from './resolveLinkOrigin';
import {
    COUNTRY_SEARCH_PARAM,
    JOB_IDS_SEARCH_PARAM,
    PRFORCE_GROUPS_SEARCH_PARAM,
    parseJobIdsSearchParam,
    serializeJobIdsSearchParam,
    upsertSearchParams,
} from './rbsTestConfigParams';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DraftResult {
    jobId: string;
    success: boolean;
    draftId?: string;
    applicationId?: string;
    error?: string;
}

interface RunResult {
    accountId: string;
    email: string;
    draftIds: string[];
    drafts: DraftResult[];
}

interface OrchestratorResponse {
    status: string;
    applicationDetails?: {
        // Absent in REVIEW_BEFORE_SUBMIT responses: that workflow stops at a
        // reviewable draft, so the orchestrator never submits an application.
        applicationId?: string;
        draftId: string;
        state: string;
    };
    errorDetails?: {
        code?: string;
        message?: string;
    };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

async function callOrchestratorAutoApply(
    accountId: string,
    jobId: string,
    prforceGroups?: string,
): Promise<{ success: boolean; draftId?: string; applicationId?: string; error?: string }> {
    const requestId = `rbs-test-${crypto.randomUUID()}`;

    // Forward the forced Proctor buckets so the orchestrator resolves pre-draft
    // Proctors (e.g. ia_rbs_relax_sq_requirement_tst) from the panel instead of
    // prod/QA allocation. Omit the field entirely when nothing is forced.
    const body: Record<string, string> = {
        accountId,
        jobId,
        requestId,
        mode: 'REVIEW_BEFORE_SUBMIT',
    };
    if (prforceGroups) {
        body.prforceGroups = prforceGroups;
    }

    const response = await fetch(
        'https://auto-apply-orchestrator.sandbox.qa.indeed.net/private/qa-test/auto-apply',
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        },
    );

    if (!response.ok) {
        const text = await response.text().catch(() => '');
        return { success: false, error: `HTTP ${response.status}: ${text.substring(0, 200)}` };
    }

    const data = (await response.json()) as OrchestratorResponse;
    const { applicationDetails, errorDetails, status: responseStatus } = data;
    const isSuccess = responseStatus === 'SUCCESS';

    if (isSuccess && applicationDetails?.draftId) {
        return {
            success: true,
            applicationId: applicationDetails.applicationId,
            draftId: applicationDetails.draftId,
        };
    }

    const errorCode = errorDetails?.code;
    const errorMsg = errorDetails?.message ?? 'Unknown error';
    return { success: false, error: errorCode ? `${errorCode}: ${errorMsg}` : errorMsg };
}

interface SignInResult {
    accountId: string;
    email: string;
}

// ── Account panel ───────────────────────────────────────────────────────────────

interface AccountPanelProps {
    email: string;
    onEmailChange: (value: string) => void;
    onSignIn: () => void;
    signingIn: boolean;
    signInResult: SignInResult | null;
    signInError: string | null;
}

/** Side-panel card to create (or reuse) a QA test account and "become" it via the
 * non-prod `/test/be-account` server endpoint. */
const AccountPanel: React.FC<AccountPanelProps> = ({
    email,
    onEmailChange,
    onSignIn,
    signingIn,
    signInResult,
    signInError,
}) => (
    <Box
        sx={{
            marginBlockStart: 8,
            padding: 4,
            border: '1px solid',
            borderColor: 'border.weak',
            borderRadius: 'md',
            backgroundColor: 'background.weaker',
            fontFamily: 'mono',
        }}
    >
        <Heading level={3} sx={{ marginBlockEnd: 2 }}>
            Test account
        </Heading>
        <Text variant="secondary" sx={{ marginBlockEnd: 4, display: 'block', fontSize: 'sm' }}>
            Creates a QA account and signs the browser in by setting session cookies on{' '}
            <code>.indeed.net</code>. Non-prod only.
        </Text>

        <Stack spacing={4} vertical>
            <InputFormField
                label="Email to create"
                helperText="Blank = random email. Reused if it already exists."
                disabled={signingIn}
                inputProps={{
                    id: 'test-account-email',
                    value: email,
                    onChange: (e) => onEmailChange(e.target.value),
                    placeholder: 'existing-account@mailosaur.io',
                    style: { width: '100%', fontFamily: 'monospace', fontSize: '13px' },
                }}
            />

            <Box>
                <Button
                    variant="primary"
                    loading={signingIn}
                    disabled={signingIn}
                    onClick={onSignIn}
                >
                    {signingIn ? 'Signing in…' : 'Create & sign in'}
                </Button>
            </Box>
        </Stack>

        {signInResult && (
            <Box sx={{ marginBlockStart: 4 }}>
                <Alert status="success">
                    Signed in as {signInResult.email} (accountId={signInResult.accountId})
                </Alert>
            </Box>
        )}

        {signInError && (
            <Box sx={{ marginBlockStart: 4 }}>
                <Alert status="critical">{signInError}</Alert>
            </Box>
        )}
    </Box>
);

// ── Component ─────────────────────────────────────────────────────────────────

// Per-country defaults. The orchestrator validates each job's country against the
// jobseeker's profile country, so the profile we set up must match the jobs being
// drafted — otherwise every draft fails with JOB_COUNTRY_MISMATCH. Each country ships
// with its own set of known-good seeded job IDs.
interface CountryConfig {
    locale: string;
    ipCountry: string;
    geoCountry: string;
    resumeCountry: string;
    postalCode: string;
    city: string;
    phoneNumber: string;
    jobIds: string[];
}

const COUNTRY_CONFIGS = {
    US: {
        locale: 'en-US',
        ipCountry: 'US',
        geoCountry: 'US',
        resumeCountry: 'US',
        postalCode: '90210',
        city: 'Beverly Hills, CA',
        phoneNumber: '+1 512 555 1234',
        jobIds: ['1000079114251', '1000079439648'],
    },
    CA: {
        locale: 'en-CA',
        ipCountry: 'CA',
        geoCountry: 'CA',
        resumeCountry: 'CA',
        postalCode: 'M5H 2N2',
        city: 'Toronto, ON',
        phoneNumber: '+1 416 555 1234',
        jobIds: ['1000072897924', '1000072897934', '1000072895982', '1000078962366'],
    },
} satisfies Record<string, CountryConfig>;

type CountryCode = keyof typeof COUNTRY_CONFIGS;

const parseCountryCode = (value: string | null): CountryCode =>
    (Object.keys(COUNTRY_CONFIGS) as CountryCode[]).includes(value as CountryCode)
        ? (value as CountryCode)
        : 'US';

const TestRbsPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [country, setCountry] = useState<CountryCode>(() =>
        parseCountryCode(searchParams.get(COUNTRY_SEARCH_PARAM)),
    );
    const [jobIdsText, setJobIdsText] = useState(() => {
        const fromUrl = parseJobIdsSearchParam(searchParams.get(JOB_IDS_SEARCH_PARAM));
        const countryCode = parseCountryCode(searchParams.get(COUNTRY_SEARCH_PARAM));
        return fromUrl !== undefined
            ? fromUrl.join('\n')
            : COUNTRY_CONFIGS[countryCode].jobIds.join('\n');
    });
    const [locale, setLocale] = useState(
        () => COUNTRY_CONFIGS[parseCountryCode(searchParams.get(COUNTRY_SEARCH_PARAM))].locale,
    );
    const [baseUrl, setBaseUrl] = useState('');
    const [usePastAnswers, setUsePastAnswers] = useState(true);
    const [loading, setLoading] = useState(false);
    const [statusMessages, setStatusMessages] = useState<string[]>([]);
    const [result, setResult] = useState<RunResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Account sign-in (non-prod `/test/be-account` endpoint).
    const [accountEmail, setAccountEmail] = useState('');
    const [signingIn, setSigningIn] = useState(false);
    const [signInResult, setSignInResult] = useState<SignInResult | null>(null);
    const [signInError, setSignInError] = useState<string | null>(null);

    // Forced proctor buckets appended to every generated link AND sent on the
    // orchestrator draft POST. Empty when nothing is forced, in which case the
    // `prforceGroups` param/field is dropped entirely. Hydrate once from the URL
    // when `prforceGroups` is present so a shared link reproduces the same buckets.
    const [initialProctors] = useState(() =>
        proctorStateFromPrforceGroups(
            searchParams.has(PRFORCE_GROUPS_SEARCH_PARAM)
                ? (searchParams.get(PRFORCE_GROUPS_SEARCH_PARAM) ?? '')
                : null,
        ),
    );
    const proctorOverrides = useProctorOverrides(SUPPORTED_PROCTORS, initialProctors);
    const { prforceGroups } = proctorOverrides;

    const replaceSearchParams = (updates: Record<string, string | null>) => {
        setSearchParams((previous) => upsertSearchParams(previous, updates), { replace: true });
    };

    const commitJobIdsToUrl = (text: string) => {
        replaceSearchParams({
            [JOB_IDS_SEARCH_PARAM]: serializeJobIdsSearchParam(text),
        });
    };

    // Built only once drafts exist (client-side), so `window` is never read during SSR.
    const draftQuery = result
        ? `${prforceGroups ? `prforceGroups=${prforceGroups}&` : ''}draftIds=${result.draftIds.join(',')}`
        : '';
    const linkOrigin = result ? resolveLinkOrigin(baseUrl, window.location.origin) : '';
    const autoApplyUrl = result ? `${linkOrigin}/auto-apply/?${draftQuery}` : '';
    const confirmationUrl = result ? `${linkOrigin}/auto-apply/confirmation?${draftQuery}` : '';

    const [fetchCurrentUser] = useTestRbsCurrentUserLazyQuery();
    const [fetchResumes] = useTestRbsGetResumesLazyQuery();
    const [createEmptyResume] = useTestRbsCreateEmptyResumeMutation();
    const [updateResume] = useTestRbsUpdateResumeMutation();
    const [addWorkExperiences] = useTestRbsAddWorkExperiencesMutation();
    const [updateAutoApply] = useTestRbsUpdateAutoApplyMutation();
    const [fetchAutoApplyInfo] = useTestRbsAutoApplyInfoLazyQuery();

    const log = (msg: string) => setStatusMessages((prev) => [...prev, msg]);

    // Switching country swaps in that country's seeded job IDs and default locale. This
    // resets any manual edits to those two fields, which is intended — the jobs must
    // match the profile country to avoid JOB_COUNTRY_MISMATCH.
    const handleCountryChange = (next: CountryCode) => {
        setCountry(next);
        setLocale(COUNTRY_CONFIGS[next].locale);
        const nextJobIdsText = COUNTRY_CONFIGS[next].jobIds.join('\n');
        setJobIdsText(nextJobIdsText);
        replaceSearchParams({
            [COUNTRY_SEARCH_PARAM]: next,
            [JOB_IDS_SEARCH_PARAM]: serializeJobIdsSearchParam(nextJobIdsText),
        });
    };

    const handleSignIn = async () => {
        setSigningIn(true);
        setSignInError(null);
        setSignInResult(null);

        try {
            const trimmed = accountEmail.trim();
            const url = trimmed
                ? `/test/be-account?email=${encodeURIComponent(trimmed)}`
                : '/test/be-account';

            const response = await fetch(url, { method: 'POST', credentials: 'include' });
            const data = (await response.json().catch(() => ({}))) as {
                accountId?: number | string;
                email?: string;
                error?: string;
            };

            if (!response.ok) {
                throw new Error(data.error || `HTTP ${response.status}`);
            }

            setSignInResult({
                accountId: String(data.accountId ?? ''),
                email: String(data.email ?? trimmed),
            });
        } catch (err) {
            setSignInError(err instanceof Error ? err.message : 'Sign-in failed');
        } finally {
            setSigningIn(false);
        }
    };

    const handleRun = async () => {
        const jobIds = jobIdsText
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);

        if (jobIds.length === 0) return;

        setLoading(true);
        setError(null);
        setResult(null);
        setStatusMessages([]);

        const countryConfig = COUNTRY_CONFIGS[country];
        const localeHeaders = { 'indeed-locale': locale };

        try {
            // Step 1: Query current user
            log('Querying current user…');
            const { data: currentUserData } = await fetchCurrentUser({
                fetchPolicy: 'network-only',
            });
            const account = currentUserData?.currentUser?.account;
            const accountId = String(account?.accountId ?? '');
            const email = String(account?.accountProfile?.email?.emailAddress ?? '');

            if (!accountId) {
                throw new Error(
                    'Could not retrieve account ID. Make sure you are signed in to QA Indeed.',
                );
            }
            log(`Signed in as: ${email} (accountId=${accountId})`);

            // Step 2: Create empty resume (or use existing one)
            log('Creating resume…');
            let resumeId: string | undefined;

            const { data: createResumeData, errors: createResumeErrors } = await createEmptyResume({
                variables: {
                    input: {
                        ipCountry: countryConfig.ipCountry,
                        geoCountry: countryConfig.geoCountry,
                        resumeType: 'INDEED_RESUME',
                    },
                },
                context: { headers: localeHeaders },
            });

            resumeId = createResumeData?.createEmptyJobSeekerProfileResume?.resume?.id ?? undefined;

            if (!resumeId) {
                const alreadyHasResume = createResumeErrors?.some((e) =>
                    e.message.includes('already has a resume'),
                );
                if (!alreadyHasResume) {
                    throw new Error(
                        `Failed to create resume: ${JSON.stringify(createResumeErrors)}`,
                    );
                }
                log('Resume already exists, fetching existing resume…');
                const { data: resumesData } = await fetchResumes({ fetchPolicy: 'network-only' });
                const resumes = resumesData?.jobSeekerProfile?.profile?.resume ?? [];
                const indeedResume =
                    resumes.find((r) => r.resumeType === 'INDEED_RESUME') ?? resumes[0];
                resumeId = indeedResume.id;
                if (!resumeId) {
                    throw new Error('Could not retrieve existing resume ID.');
                }
            }
            log(`Resume ready (id=${resumeId})`);

            // Step 3: Update resume with profile info
            log('Updating resume…');
            await updateResume({
                variables: {
                    input: {
                        resume: {
                            id: resumeId,
                            firstName: 'Rbs',
                            lastName: 'DraftUser',
                            phoneNumber: countryConfig.phoneNumber,
                            location: {
                                address: '',
                                country: countryConfig.resumeCountry,
                                postalCode: countryConfig.postalCode,
                                city: countryConfig.city,
                            },
                        },
                    },
                },
            });
            log('Resume updated');

            // Step 4: Add work experience
            log('Adding work experience…');
            await addWorkExperiences({
                variables: {
                    input: {
                        resumeId,
                        workExperiences: [{ company: 'Test Company', title: 'Software Engineer' }],
                    },
                },
                context: { headers: localeHeaders },
            });
            log('Work experience added');

            // Step 5: Enable auto-apply with REVIEW_BEFORE_SUBMIT mode
            log('Enabling auto-apply…');
            const ctk = crypto.randomUUID().replace(/-/g, '');
            await updateAutoApply({
                variables: {
                    input: {
                        autoApplyStatus: 'ENABLED',
                        selectedResumeType: 'INDEED_RESUME',
                        mode: 'REVIEW_BEFORE_SUBMIT',
                        trackApplicantInfo: false,
                        ip: '127.0.0.1',
                        locale,
                        // JOB_COUNTRY_MISMATCH is validated against the applicant-info
                        // `locale` (above), not this `country` field; `country` is sent only
                        // to keep the preference consistent with the selected config.
                        country: countryConfig.geoCountry,
                        ctk,
                        sqPastAnswerPrefillPreference: usePastAnswers ? 'OPT_IN' : 'OPT_OUT',
                    },
                },
                context: { headers: { ...localeHeaders, 'indeed-ctk': ctk } },
            });
            log('Auto-apply enabled');

            // Step 5b: Log the stored preferences — the applicant-info locale (the value the
            // orchestrator uses to derive jobseeker country for the JOB_COUNTRY_MISMATCH check)
            // and the past-answer prefill consent (OPT_IN/OPT_OUT the tester just chose).
            const { data: infoData } = await fetchAutoApplyInfo({ fetchPolicy: 'network-only' });
            const storedLocale = infoData?.autoApplyInfo?.applicantInfo?.locale ?? '(none)';
            log(`Stored applicant-info locale: ${storedLocale} (sent locale=${locale})`);
            const storedSqPreference =
                infoData?.autoApplyInfo?.sqPastAnswerPrefillPreference ?? '(none)';
            log(`Stored past-answer prefill preference: ${storedSqPreference}`);

            // Step 6: Create drafts via orchestrator HTTP API
            log('Creating drafts via orchestrator…');
            const drafts: DraftResult[] = [];

            for (const jobId of jobIds) {
                log(`  Creating draft for job ${jobId}…`);
                const draftResult = await callOrchestratorAutoApply(
                    accountId,
                    jobId,
                    prforceGroups || undefined,
                );
                drafts.push({ jobId, ...draftResult });
                log(
                    `  → ${draftResult.success ? `draftId=${draftResult.draftId}` : `error: ${draftResult.error}`}`,
                );
            }

            const draftIds = drafts
                .filter((d) => d.success && d.draftId)
                .map((d) => d.draftId as string);

            log(`Done. ${draftIds.length} draft(s) created.`);

            setResult({ accountId, email, draftIds, drafts });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const isRunDisabled = loading || jobIdsText.trim().length === 0;

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                maxInlineSize: '1100px',
                margin: '0 auto',
            }}
        >
            <Box sx={{ flex: '1 1 460px', minInlineSize: 0, padding: 8, fontFamily: 'mono' }}>
                <Heading level={1} sx={{ marginBlockEnd: 2 }}>
                    RBS Flow Testing
                </Heading>
                <Text variant="secondary" sx={{ marginBlockEnd: 8, display: 'block' }}>
                    Sets up a <strong>REVIEW_BEFORE_SUBMIT</strong> profile and creates drafts via
                    the orchestrator HTTP API for the currently signed-in QA account.
                    <br />
                    Job country, Job IDs, and Proctor overrides sync to the URL (
                    <code>{COUNTRY_SEARCH_PARAM}</code>, <code>{JOB_IDS_SEARCH_PARAM}</code>,{' '}
                    <code>{PRFORCE_GROUPS_SEARCH_PARAM}</code>) so you can bookmark or share a
                    configuration.
                    <br />
                    Only available in non-production environments.
                </Text>

                <Stack spacing={6} vertical>
                    <Box>
                        <label
                            htmlFor="job-country"
                            style={{
                                display: 'block',
                                fontFamily: 'monospace',
                                fontSize: '13px',
                                fontWeight: 700,
                                marginBlockEnd: '4px',
                            }}
                        >
                            Job country
                        </label>
                        <select
                            id="job-country"
                            data-testid="job-country"
                            value={country}
                            disabled={loading}
                            onChange={(e) => handleCountryChange(e.target.value as CountryCode)}
                            style={{
                                width: '240px',
                                fontFamily: 'monospace',
                                fontSize: '13px',
                                padding: '4px',
                            }}
                        >
                            {Object.keys(COUNTRY_CONFIGS).map((code) => (
                                <option key={code} value={code}>
                                    {code}
                                </option>
                            ))}
                        </select>
                        <Text
                            variant="secondary"
                            sx={{ display: 'block', fontSize: 'xs', marginBlockStart: 2 }}
                        >
                            Sets the jobseeker profile country (IP, geo, resume location) and swaps
                            in that country&apos;s seeded job IDs. Must match the jobs&apos; country
                            or drafts fail with JOB_COUNTRY_MISMATCH.
                        </Text>
                    </Box>

                    <TextAreaFormField
                        label="Job IDs"
                        helperText="One job ID per line. Each creates one draft. Saved to the URL on blur."
                        disabled={loading}
                        textareaProps={{
                            id: 'job-ids',
                            rows: 6,
                            value: jobIdsText,
                            onChange: (e) => setJobIdsText(e.target.value),
                            onBlur: (e) => commitJobIdsToUrl(e.target.value),
                            placeholder: 'One job ID per line\n1000079114251\n1000079439648',
                            style: { fontFamily: 'monospace', fontSize: '13px' },
                        }}
                    />

                    <InputFormField
                        label="Locale"
                        disabled={loading}
                        inputProps={{
                            id: 'locale',
                            value: locale,
                            onChange: (e) => setLocale(e.target.value),
                            style: { width: '240px', fontFamily: 'monospace', fontSize: '13px' },
                        }}
                    />

                    <InputFormField
                        label="Base URL"
                        helperText="Leave blank to use this host. Paste a lemma origin to generate Auto Apply / Confirmation links against that host instead of QA."
                        inputProps={{
                            id: 'link-base-url',
                            'data-testid': 'link-base-url',
                            value: baseUrl,
                            onChange: (e) => setBaseUrl(e.target.value),
                            placeholder: 'https://lemma-….temp.awscmhqa4.k8s.indeed.tech',
                            style: {
                                width: '100%',
                                fontFamily: 'monospace',
                                fontSize: '13px',
                            },
                        }}
                    />

                    <Box>
                        <Checkbox
                            id="use-past-answers"
                            checked={usePastAnswers}
                            disabled={loading}
                            onChange={(event) => setUsePastAnswers(event.target.checked)}
                        >
                            Use past screener answers
                        </Checkbox>
                        <Text
                            variant="secondary"
                            sx={{ display: 'block', fontSize: 'xs', marginBlockStart: 2 }}
                        >
                            Checked stores <code>OPT_IN</code> (reuse 3P/IQv2 past answers);
                            unchecked stores <code>OPT_OUT</code> (unprefilled screener questions).
                            Proctor cannot override this consent.
                        </Text>
                    </Box>

                    <Box>
                        <Button
                            variant="primary"
                            loading={loading}
                            disabled={isRunDisabled}
                            onClick={handleRun}
                        >
                            {loading ? 'Running…' : 'Run'}
                        </Button>
                    </Box>
                </Stack>

                {statusMessages.length > 0 && (
                    <Box
                        sx={{
                            marginBlockStart: 6,
                            padding: 4,
                            backgroundColor: 'background.weaker',
                            border: '1px solid',
                            borderColor: 'border.weak',
                            borderRadius: 'md',
                            fontFamily: 'mono',
                            fontSize: 'sm',
                            lineHeight: 1.8,
                        }}
                    >
                        {statusMessages.map((msg, i) => (
                            <div key={i}>{msg}</div>
                        ))}
                    </Box>
                )}

                {error && (
                    <Box sx={{ marginBlockStart: 6 }}>
                        <Alert status="critical">Error: {error}</Alert>
                    </Box>
                )}

                {result && (
                    <Box sx={{ marginBlockStart: 8 }}>
                        <Heading level={2} sx={{ marginBlockEnd: 4 }}>
                            Results
                        </Heading>

                        <Box
                            sx={{
                                padding: 4,
                                backgroundColor: 'background.primary.weak',
                                border: '1px solid',
                                borderColor: 'border.primary',
                                borderRadius: 'md',
                                marginBlockEnd: 6,
                                lineHeight: 1.8,
                                fontFamily: 'mono',
                                fontSize: 'sm',
                            }}
                        >
                            <div>
                                <strong>Account ID:</strong>{' '}
                                <span id="result-account-id">{result.accountId}</span>
                            </div>
                            <div>
                                <strong>Email:</strong>{' '}
                                <span id="result-email">{result.email}</span>
                            </div>
                        </Box>

                        {result.draftIds.length > 0 && (
                            <Box>
                                <Heading level={3} sx={{ marginBlockEnd: 3 }}>
                                    Auto Apply URL
                                </Heading>
                                <ol
                                    style={{
                                        marginBlockEnd: '12px',
                                        paddingInlineStart: '20px',
                                        lineHeight: 1.8,
                                    }}
                                >
                                    <li>
                                        Visit{' '}
                                        <a
                                            data-testid="phone-verification-url"
                                            href="https://m5.apply.qa.indeed.net/indeedapply/private/?prforceGroups=ia_phone_number_verification_tst0"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            https://m5.apply.qa.indeed.net/indeedapply/private/?prforceGroups=ia_phone_number_verification_tst0
                                        </a>{' '}
                                        to disable phone verification
                                    </li>
                                    <li>Open Auto Apply URL below</li>
                                </ol>
                                <Box
                                    sx={{
                                        padding: 4,
                                        backgroundColor: 'background.weaker',
                                        border: '1px solid',
                                        borderColor: 'border.weak',
                                        borderRadius: 'md',
                                        fontFamily: 'mono',
                                        fontSize: 'sm',
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    <a
                                        id="result-auto-apply-url"
                                        data-testid="result-auto-apply-url"
                                        href={autoApplyUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {autoApplyUrl}
                                    </a>
                                </Box>

                                <Heading level={3} sx={{ marginBlock: '4 3' }}>
                                    Confirmation URL
                                </Heading>
                                <Box
                                    sx={{
                                        padding: 4,
                                        backgroundColor: 'background.weaker',
                                        border: '1px solid',
                                        borderColor: 'border.weak',
                                        borderRadius: 'md',
                                        fontFamily: 'mono',
                                        fontSize: 'sm',
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    <a
                                        id="result-confirmation-url"
                                        data-testid="result-confirmation-url"
                                        href={confirmationUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {confirmationUrl}
                                    </a>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <Box sx={{ flex: '0 0 300px', minInlineSize: 0 }}>
                <AccountPanel
                    email={accountEmail}
                    onEmailChange={setAccountEmail}
                    onSignIn={handleSignIn}
                    signingIn={signingIn}
                    signInResult={signInResult}
                    signInError={signInError}
                />

                <ProctorOverridesPanel
                    controller={proctorOverrides}
                    onCommit={(nextPrforceGroups) => {
                        replaceSearchParams({
                            [PRFORCE_GROUPS_SEARCH_PARAM]: nextPrforceGroups,
                        });
                    }}
                    description={
                        <>
                            Forced buckets are sent as <code>prforceGroups</code> on the
                            orchestrator draft POST <em>and</em> appended to the generated Auto
                            Apply / review links. Values sync to the page URL on blur so a shared
                            link restores this panel. Set a proctor to <code>-1</code> to leave it
                            unforced.
                        </>
                    }
                />
            </Box>
        </Box>
    );
};

export default TestRbsPage;
